import { useEffect, useRef } from 'react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';

const SCANNER_ID = 'shared-qr-scanner';

export default function CameraScanner({ onDetect, onClose }) {
    const onDetectRef = useRef(onDetect);
    const onCloseRef  = useRef(onClose);
    onDetectRef.current = onDetect;
    onCloseRef.current  = onClose;

    useEffect(() => {
        const scanner = new Html5Qrcode(SCANNER_ID, {
            formatsToSupport: [
                Html5QrcodeSupportedFormats.QR_CODE,
                Html5QrcodeSupportedFormats.CODE_128,
                Html5QrcodeSupportedFormats.CODE_39,
                Html5QrcodeSupportedFormats.CODE_93,
                Html5QrcodeSupportedFormats.EAN_13,
                Html5QrcodeSupportedFormats.UPC_A,
            ],
            // Use the browser's native BarcodeDetector when available
            // (Chrome desktop, Chrome Android, recent iOS Safari). Much
            // faster + far more reliable on longer Code128 payloads —
            // the OH-<15 digits> manager barcode is 18 chars and the
            // pure-JS decoder often loses it under camera shake / lower
            // light. Falls back automatically to WASM if unsupported.
            experimentalFeatures: { useBarCodeDetectorIfSupported: true },
        });
        let stopped = false;

        const stopScanner = () => {
            if (stopped) return Promise.resolve();
            stopped = true;
            return scanner.stop().catch(() => {});
        };

        // Dynamic qrbox — 80% of the shortest viewport dimension gives long
        // Code128 (18-char OH-*) enough room without cropping. Cap so the
        // preview isn't dominated by the reticle on desktop.
        const shortEdge = Math.min(window.innerWidth || 400, window.innerHeight || 400);
        const qrBoxWidth  = Math.min(Math.floor(shortEdge * 0.85), 620);
        const qrBoxHeight = Math.min(Math.floor(qrBoxWidth * 0.35), 240);

        scanner.start(
            { facingMode: 'environment' },
            {
                fps: 24,
                qrbox: { width: qrBoxWidth, height: qrBoxHeight },
                aspectRatio: 1.777,
            },
            (text) => { stopScanner().finally(() => onDetectRef.current(text)); },
            () => {}
        ).catch(() => { onCloseRef.current(); });

        return () => { stopScanner(); };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="pkp-camera-overlay">
            <div className="pkp-camera-header">
                <button onClick={onClose} className="pkp-camera-close">✕ Close</button>
                <span className="pkp-camera-title">Scan Barcode</span>
                <span style={{ width: 60 }} />
            </div>
            <div id={SCANNER_ID} className="pkp-camera-video" />
        </div>
    );
}
